import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import LaunchIcon from '@mui/icons-material/Launch'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material'
import { FC, useEffect, useState } from 'react'
import {
  CUSTOMER_PIPELINE_STAGES,
  CostumerType,
  EnrichedGSHOrderType,
  GHLStageType,
  UserInfoType,
  fetchCostumerScenes,
} from '../../../api/appAPI'
import { CompanyType } from '../../../redux/appReducer'

interface CustomerSceneType {
  timestamp: string
  hash: string
  sceneName: string
  hasPdf?: boolean
}

interface IProps {
  open: boolean
  costumer: CostumerType
  company?: CompanyType
  usersInfo: UserInfoType[]
  enrichedOrders?: EnrichedGSHOrderType[]
  pipelineStages?: GHLStageType[]
  loadingOrders?: boolean
  onClose(): void
  onOpenDesign?(id: number): void
}

const MiniStepper: FC<{
  stages: GHLStageType[]
  activeStep: number
}> = ({ stages, activeStep }) => {
  if (!stages || stages.length === 0) return null

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        ml: 1.5,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        backgroundColor: '#f5f7fa',
        border: '1px solid #e3e8ee',
      }}
      onClick={(e) => {
        e.stopPropagation()
      }}
    >
      {stages.map((stage, idx) => {
        const isCompleted = activeStep > idx
        const isActive = activeStep === idx

        return (
          <Box
            key={stage.id || idx}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Tooltip
              title={`${idx + 1}. ${stage.name}${isActive ? ' (Current)' : ''}`}
              arrow
              placement="top"
            >
              <Box
                sx={{
                  width: isActive ? 12 : 8,
                  height: isActive ? 12 : 8,
                  borderRadius: '50%',
                  backgroundColor: isActive
                    ? '#1976d2'
                    : isCompleted
                      ? '#2e7d32'
                      : '#bdbdbd',
                  boxShadow: isActive
                    ? '0 0 0 3px rgba(25, 118, 210, 0.25)'
                    : 'none',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
            {idx < stages.length - 1 && (
              <Box
                sx={{
                  width: 14,
                  height: 2,
                  backgroundColor: isCompleted ? '#2e7d32' : '#e0e0e0',
                  mx: 0.25,
                }}
              />
            )}
          </Box>
        )
      })}
    </Box>
  )
}

const DetailRow: FC<{
  label: string
  value: string | number | undefined | null
}> = ({ label, value }) => {
  const displayVal =
    value === undefined || value === null || value === '' ? '' : String(value)
  return (
    <Typography
      sx={{
        fontSize: '0.92rem',
        color: '#222',
        mb: 0.5,
        lineHeight: 1.45,
      }}
    >
      <Box component="span" sx={{ color: '#444' }}>
        {label}
      </Box>{' '}
      - {displayVal}
    </Typography>
  )
}

const ViewCostumer: FC<IProps> = (props) => {
  const { costumer, company, usersInfo } = props

  const pipelineStages =
    props.pipelineStages && props.pipelineStages.length > 0
      ? props.pipelineStages
      : CUSTOMER_PIPELINE_STAGES

  const [loading, setLoading] = useState(false)
  const [scenes, setScenes] = useState<CustomerSceneType[]>([])
  const [copiedHash, setCopiedHash] = useState<string | null>(null)
  const [filterOrder, setFilterOrder] = useState(true)
  const [filterPdf, setFilterPdf] = useState(true)
  const [filterRegular, setFilterRegular] = useState(true)

  const getMatchedOrder = (sceneHash: string) => {
    if (!props.enrichedOrders || !sceneHash) return null
    return (
      props.enrichedOrders.find((ord) => {
        if (ord.sceneHash && ord.sceneHash === sceneHash) return true
        if (ord.note) {
          const parts = ord.note.split('||')
          if (parts.length > 1 && parts[1]) {
            const h = parts[1].split(':')[1]?.trim()
            return h === sceneHash
          }
        }
        return false
      }) || null
    )
  }

  const filteredScenes = scenes.filter((scene) => {
    const matchedOrder = getMatchedOrder(scene.hash)
    const isOrder = Boolean(matchedOrder)
    const isPdf = Boolean(scene.hasPdf)
    const isRegular = !isOrder && !isPdf

    return (
      (filterOrder && isOrder) ||
      (filterPdf && isPdf) ||
      (filterRegular && isRegular)
    )
  })

  const handleCopyPdfUrl = (hash: string, url: string) => {
    navigator.clipboard.writeText(url)
    setCopiedHash(hash)
    setTimeout(() => {
      setCopiedHash((prev) => (prev === hash ? null : prev))
    }, 2000)
  }

  const handleDownloadPdf = async (url: string, rawFilename: string) => {
    const filename = rawFilename.replace(/[/\\?%*:|"<>]/g, '-').trim()
    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error('Download failed')
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    } catch {
      const link = document.createElement('a')
      link.href = url
      link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  useEffect(() => {
    if (!props.open || !costumer?.email) {
      setScenes([])
      setLoading(false)
      return
    }

    let isMounted = true
    setLoading(true)

    fetchCostumerScenes(costumer.email)
      .then((data) => {
        if (!isMounted) return
        let list: CustomerSceneType[] = []
        if (Array.isArray(data)) {
          list = data
        } else if (typeof data === 'string') {
          try {
            const parsed = JSON.parse(data)
            if (Array.isArray(parsed)) {
              list = parsed
            }
          } catch {
            list = []
          }
        }
        const enrichedList = list.map((item) => ({
          ...item,
          hasPdf: Boolean(
            (item.sceneName && item.sceneName.includes('#Locked')) ||
            item.hasPdf
          ),
        }))
        setScenes(enrichedList)
      })
      .catch((error) => {
        console.error('Error fetching customer scenes:', error)
        if (isMounted) {
          setScenes([])
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [props.open, costumer?.email])

  const creatorUser = [
    {
      email: company?.email || '',
      name: company?.ownerName || '',
      phone: '',
    },
    ...usersInfo,
  ].find((u) => u.email === costumer.creatorEmail)

  const companyName =
    company?.name ||
    usersInfo.find((u) => u.email === costumer.companyEmail)?.companyName ||
    ''

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '92vw',
          maxWidth: 1600,
          height: '92vh',
          maxHeight: '92vh',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 600, color: '#222' }}
        >
          Customer | {costumer.firstName} {costumer.lastName}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={props.onClose}
          size="small"
          sx={{
            color: '#888',
            '&:hover': { color: '#333', backgroundColor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'row',
          flex: 1,
          overflow: 'hidden',
        }}
      >
        {/* Left Column: Customer Info */}
        <Box
          sx={{
            width: '20%',
            minWidth: 340,
            p: 3,
            overflowY: 'auto',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              fontSize: '1.05rem',
              color: '#222',
              mb: 1,
            }}
          >
            Customer Details
          </Typography>
          <DetailRow
            label="name"
            value={`${costumer.firstName} ${costumer.lastName}`.trim()}
          />
          <DetailRow label="email" value={costumer.email} />
          <DetailRow label="company name" value={companyName} />
          <DetailRow label="company email" value={costumer.companyEmail} />
          <DetailRow label="status" value={costumer.status} />
          <DetailRow label="phone" value={costumer.phone} />
          <DetailRow label="address1" value={costumer.address1} />
          <DetailRow label="address2" value={costumer.address2} />
          <DetailRow label="zip" value={costumer.zip} />
          <DetailRow label="city" value={costumer.city} />
          <DetailRow label="province" value={costumer.province} />
          {costumer.country && (
            <DetailRow label="country" value={costumer.country} />
          )}

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              fontSize: '1.05rem',
              color: '#222',
              mt: 2.5,
              mb: 1,
            }}
          >
            User Details
          </Typography>
          <DetailRow label="name" value={creatorUser?.name || ''} />
          <DetailRow label="email" value={costumer.creatorEmail} />
          <DetailRow label="phone" value={creatorUser?.phone || null} />

          {costumer.note && (
            <>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  color: '#222',
                  mt: 2.5,
                  mb: 1,
                }}
              >
                Notes
              </Typography>
              <DetailRow label="note" value={costumer.note} />
            </>
          )}
        </Box>

        {/* Vertical Divider */}
        <Divider
          orientation="vertical"
          flexItem
          sx={{ borderColor: '#e0e0e0' }}
        />

        {/* Right Column: Designs Section */}
        <Box
          sx={{
            flex: 1,
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                fontSize: '1.05rem',
                color: '#222',
              }}
            >
              Designs
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={filterOrder}
                    onChange={(e) => setFilterOrder(e.target.checked)}
                    sx={{
                      p: 0.5,
                      color: '#9e9e9e',
                      '&.Mui-checked': { color: '#1976d2' },
                    }}
                  />
                }
                label="Order"
                sx={{
                  m: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: '#444',
                    userSelect: 'none',
                  },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={filterPdf}
                    onChange={(e) => setFilterPdf(e.target.checked)}
                    sx={{
                      p: 0.5,
                      color: '#9e9e9e',
                      '&.Mui-checked': { color: '#1976d2' },
                    }}
                  />
                }
                label="PDF"
                sx={{
                  m: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: '#444',
                    userSelect: 'none',
                  },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={filterRegular}
                    onChange={(e) => setFilterRegular(e.target.checked)}
                    sx={{
                      p: 0.5,
                      color: '#9e9e9e',
                      '&.Mui-checked': { color: '#1976d2' },
                    }}
                  />
                }
                label="Regular"
                sx={{
                  m: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: '#444',
                    userSelect: 'none',
                  },
                }}
              />
            </Box>
          </Box>

          {loading || Boolean(props.loadingOrders) ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                gap: 2,
              }}
            >
              <CircularProgress size={36} />
              <Typography variant="body2" sx={{ color: '#888' }}>
                Loading designs...
              </Typography>
            </Box>
          ) : scenes.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                color: '#888',
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontStyle: 'italic', color: '#888' }}
              >
                No designs available
              </Typography>
            </Box>
          ) : filteredScenes.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                color: '#888',
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontStyle: 'italic', color: '#888' }}
              >
                No designs match the selected filters
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {filteredScenes.map((scene, index) => {
                const matchedOrder = getMatchedOrder(scene.hash)
                const hasPdf = !!scene.hasPdf
                const pdfUrl = scene.hash
                  ? 'https://www.thegarageshop.com/dt/designsPdfs/' +
                    scene.hash +
                    '.pdf'
                  : ''

                return (
                  <Accordion
                    key={scene.hash || index}
                    disableGutters
                    sx={{
                      // backgroundColor: '#f8f9fa',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px !important',
                      '&:before': { display: 'none' },
                      boxShadow: 'none',
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        // backgroundColor: '#f8f9fa',
                        minHeight: 48,
                        '& .MuiAccordionSummary-content': {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          pr: 1,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          sx={{ fontWeight: 500, fontSize: '0.95rem' }}
                        >
                          {scene.sceneName || 'Untitled Design'}
                        </Typography>
                        {matchedOrder && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              ml: 1,
                            }}
                          >
                            {/* <Chip
                              label={matchedOrder.name}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 22,
                                color: '#1976d2',
                                borderColor: '#1976d2',
                              }}
                            /> */}
                            {pipelineStages &&
                              pipelineStages.length > 0 && (
                                <MiniStepper
                                  stages={pipelineStages}
                                  activeStep={matchedOrder.stageIndex ?? 0}
                                />
                              )}
                          </Box>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {hasPdf && (
                          <Tooltip title="Has PDF">
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: '0.78rem',
                                color: '#475569',
                                letterSpacing: 0.5,
                              }}
                            >
                              PDF
                            </Typography>
                          </Tooltip>
                        )}
                        {scene.hash && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              window.open('/dt?h=' + scene.hash, '_blank')
                            }}
                            title="Open Design"
                            sx={{ p: 0.5 }}
                          >
                            <LaunchIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        position: 'relative',
                        backgroundColor: '#f8f9fa',
                        boxSizing: 'border-box',
                        pt: 1.5,
                        pb: 2,
                        px: 2,
                        borderTop: '1px solid #ebebeb',
                      }}
                    >
                      {hasPdf && pdfUrl && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 16,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            backgroundColor: '#ffffff',
                            border: '1px solid #e0e0e0',
                            borderRadius: 1.5,
                            px: 1,
                            py: 0.5,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            zIndex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.78rem',
                                color: '#475569',
                                letterSpacing: 0.5,
                              }}
                            >
                              PDF
                            </Typography>
                          </Box>

                          <Divider
                            orientation="vertical"
                            flexItem
                            sx={{ mx: 0.5, height: 16 }}
                          />

                          <Tooltip title="Open PDF">
                            <IconButton
                              size="small"
                              onClick={() => {
                                window.open(pdfUrl, '_blank')
                              }}
                              sx={{
                                p: 0.5,
                                color: '#555',
                                '&:hover': { color: '#1976d2' },
                              }}
                            >
                              <LaunchIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip
                            title={
                              copiedHash === scene.hash
                                ? 'Copied!'
                                : 'Copy PDF Link'
                            }
                          >
                            <IconButton
                              size="small"
                              onClick={() => {
                                handleCopyPdfUrl(scene.hash, pdfUrl)
                              }}
                              sx={{
                                p: 0.5,
                                color:
                                  copiedHash === scene.hash
                                    ? '#2e7d32'
                                    : '#555',
                                '&:hover': { color: '#1976d2' },
                              }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Download PDF">
                            <IconButton
                              size="small"
                              onClick={() => {
                                handleDownloadPdf(
                                  pdfUrl,
                                  `${scene.sceneName || 'design'}.pdf`
                                )
                              }}
                              sx={{
                                p: 0.5,
                                color: '#555',
                                '&:hover': { color: '#1976d2' },
                              }}
                            >
                              <ArrowDownwardIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}

                      <DetailRow
                        label="name"
                        value={scene.sceneName || 'Untitled Design'}
                      />
                      <DetailRow label="date" value={scene.timestamp} />
                      <DetailRow label="hash" value={scene.hash} />
                      {matchedOrder && (
                        <>
                          {/* <DetailRow
                            label="order name"
                            value={matchedOrder.name}
                          /> */}
                          {pipelineStages &&
                            pipelineStages.length > 0 && (
                              <Box
                                sx={{
                                  boxSizing: 'border-box',
                                  width: '100%',
                                  mt: 2.5,
                                  mb: 1.5,
                                  p: 2,
                                  backgroundColor: '#ffffff',
                                  borderRadius: 1.5,
                                  border: '1px solid #ebebeb',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    color: '#555',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    display: 'block',
                                    mb: 1.5,
                                    px: 0.5,
                                  }}
                                >
                                  Order Pipeline Stage:{' '}
                                  {matchedOrder.stageName || 'In Progress'}
                                </Typography>
                                <Stepper
                                  activeStep={matchedOrder.stageIndex ?? 0}
                                  alternativeLabel
                                  sx={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    '& .MuiStep-root': {
                                      boxSizing: 'border-box',
                                      minWidth: 0,
                                      px: 0.5,
                                    },
                                    '& .MuiStepConnector-root': {
                                      top: '12px',
                                      left: 'calc(-50% + 12px)',
                                      right: 'calc(50% + 12px)',
                                    },
                                    '& .MuiStepIcon-root': {
                                      width: 24,
                                      height: 24,
                                      fontSize: '0.8rem',
                                    },
                                    '& .MuiStepLabel-label': {
                                      fontSize: '0.78rem',
                                      lineHeight: 1.25,
                                      mt: 0.75,
                                      wordBreak: 'break-word',
                                    },
                                    '& .MuiStepLabel-label.Mui-active': {
                                      fontWeight: 600,
                                      color: '#1976d2',
                                    },
                                    '& .MuiStepLabel-label.Mui-completed': {
                                      fontWeight: 500,
                                      color: '#2e7d32',
                                    },
                                    '& .MuiStepIcon-root.Mui-completed': {
                                      color: '#2e7d32',
                                    },
                                    '& .MuiStepIcon-root.Mui-active': {
                                      color: '#1976d2',
                                    },
                                  }}
                                >
                                  {pipelineStages.map((stage, idx) => (
                                    <Step
                                      key={stage.id || idx}
                                      completed={
                                        (matchedOrder.stageIndex ?? 0) > idx
                                      }
                                    >
                                      <StepLabel>{stage.name}</StepLabel>
                                    </Step>
                                  ))}
                                </Stepper>
                              </Box>
                            )}
                        </>
                      )}
                      {/* {scene.hash && (
                        <Box
                          sx={{
                            mt: 1.5,
                            display: 'flex',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            endIcon={<LaunchIcon sx={{ fontSize: 16 }} />}
                            onClick={() => {
                              window.open('/dt?h=' + scene.hash, '_blank')
                            }}
                            sx={{
                              textTransform: 'uppercase',
                              fontWeight: 600,
                              fontSize: '0.8rem',
                            }}
                          >
                            Open Design
                          </Button>
                        </Box>
                      )} */}
                    </AccordionDetails>
                  </Accordion>
                )
              })}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ViewCostumer
